import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Outlet, useMatches } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";

import { SectionSelectPortalProvider } from "~/components/section_side_nav";

import { Menu } from "./Menu";
import { useClarity } from "./use_clarity";

export function AppLayout() {
  useClarity();

  const matches = useMatches();
  const crumbs = matches
    .filter((match) => match.routeId !== "__root__")
    .map((match) => {
      let title = "Unknown Title";
      if (
        Array.isArray(match.meta) &&
        match.meta.length > 0 &&
        match.meta[0]?.title
      ) {
        title = match.meta[0].title;
      }

      return {
        id: match.routeId,
        pathname: match.pathname ?? "#",
        label: title,
      };
    });

  const fallbackTitle = useMemo(() => {
    if (crumbs.length > 0) {
      return crumbs[crumbs.length - 1].label;
    }
    return "Admin Console";
  }, [crumbs]);

  const [resolvedTitle, setResolvedTitle] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    let isActive = true;
    const matchWithPromise = [...matches]
      .reverse()
      .find((m) => m.loaderData?.titlePromise instanceof Promise);

    if (!matchWithPromise) {
      setResolvedTitle(undefined);
      return () => {
        isActive = false;
      };
    }

    const titlePromise = matchWithPromise.loaderData?.titlePromise;
    if (titlePromise instanceof Promise) {
      titlePromise.then((title: string | undefined) => {
        if (isActive) {
          setResolvedTitle(title);
        }
      });
    }

    return () => {
      isActive = false;
    };
  }, [matches]);

  const currentTitle = resolvedTitle ?? fallbackTitle;

  useEffect(() => {
    document.title = currentTitle;
  }, [currentTitle]);

  const sectionSelectRef = useRef<HTMLDivElement>(null);

  const hasReducedMobileTopPadding = matches.some(
    (match) =>
      (match.staticData as { reducedMobileTopPadding?: boolean } | undefined)
        ?.reducedMobileTopPadding === true,
  );

  const isStandalone = matches.some(
    (match) =>
      (match.staticData as { isStandalone?: boolean } | undefined)
        ?.isStandalone === true,
  );

  if (isStandalone) {
    return (
      <Box component="main" sx={{ height: "100vh", overflow: "auto" }}>
        <Outlet />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        height: "100vh",
        overflow: "hidden",
      }}
    >
      <Menu />
      <SectionSelectPortalProvider value={sectionSelectRef}>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            px: { xs: 2, sm: 3, md: 5, lg: 8 },
            pt: { xs: hasReducedMobileTopPadding ? 0 : 2, md: 3 },
            pb: { xs: 2, md: 3 },
            minWidth: 0,
            minHeight: 0,
            height: "100%",
            overflow: "auto",
          }}
        >
          <div ref={sectionSelectRef} style={{ display: "contents" }} />
          <Typography variant="h4SolidDivider" sx={{ mb: { xs: 3, md: 5 } }}>
            {currentTitle}
          </Typography>
          <Outlet />
        </Box>
      </SectionSelectPortalProvider>
    </Box>
  );
}
